import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Brand-aligned initials avatar shown when an agent photo is missing or fails to load.
 * Stable colour per name (hash of name → palette index) so the same agent always
 * gets the same fallback colour instead of flashing.
 */
const PALETTE = [
  ['#003D5C', '#FFFFFF'], // navy
  ['#0077B6', '#FFFFFF'], // primary blue
  ['#D4AF37', '#003D5C'], // gold
  ['#5B7C99', '#FFFFFF'], // slate
  ['#1A8F8F', '#FFFFFF'], // teal
  ['#7E3F8F', '#FFFFFF'], // plum
];

const _hash = (s) => {
  let h = 0;
  for (let i = 0; i < (s || '').length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

const _initials = (name) => {
  if (!name) return '?';
  const parts = name.replace(/['"`]/g, '').split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/**
 * Normalise a stored photo URL. Handles 3 historical shapes:
 *   1. Empty / undefined / 'uploading...'  →  ''  (falls back to initials)
 *   2. Already-absolute (https://...)      →  as-is
 *   3. Relative `/api/uploads/...` saved by current upload route → prepend REACT_APP_BACKEND_URL
 *
 * Stale absolute URLs pointing at dead preview subdomains
 * (e.g. https://va-timetrack-dev.preview.emergentagent.com/...) will fail the onError
 * handler and the initials avatar takes over — so the UI never shows a broken-image icon.
 */
const _normaliseSrc = (raw) => {
  const s = (raw || '').trim();
  if (!s || s === 'uploading...') return '';
  if (s.startsWith('data:') || s.startsWith('http://') || s.startsWith('https://')) return s;
  if (s.startsWith('/')) return `${API_URL}${s}`;
  return s;
};

/**
 * Drop-in <img> replacement with a graceful initials fallback.
 *
 * Props:
 *   - src: any of the 3 photo shapes documented above
 *   - name: full agent / team-member name (used for both alt and initials)
 *   - className: any image classes you'd normally pass (object-cover etc.)
 *   - rounded: 'rounded-full' | 'rounded-lg' | '' (default '')
 */
export const TeamPhoto = ({ src, name, className = 'w-full h-full object-cover object-top', rounded = '' }) => {
  const initialSrc = _normaliseSrc(src);
  const [errored, setErrored] = useState(!initialSrc);

  // If the src prop changes (e.g. after a save), retry image load
  useEffect(() => {
    setErrored(!initialSrc);
  }, [initialSrc]);

  if (errored) {
    const [bg, fg] = PALETTE[_hash(name || '') % PALETTE.length];
    return (
      <div
        className={`flex items-center justify-center select-none ${className} ${rounded}`}
        style={{ backgroundColor: bg, color: fg }}
        aria-label={name || 'No photo'}
        data-testid="team-photo-fallback"
      >
        <span className="font-bold tracking-wide" style={{ fontSize: 'clamp(20px, 30%, 64px)' }}>
          {_initials(name)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={initialSrc}
      alt={name || 'Team member'}
      className={`${className} ${rounded}`}
      onError={() => setErrored(true)}
      loading="lazy"
    />
  );
};

export default TeamPhoto;
